import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getEffectiveSchedule, setHomeJob } from '@/lib/api/scheduling';
import { useToast } from '@/lib/hooks/useToast';

// Compact home-job attribution control for the employee detail Schedule section,
// bringing the core scheduling model (a "home job" that applies every workday
// unless overridden) onto the employee page — not just the /scheduling board.
export function EmployeeHomeJobSetter({ userId }: { userId: number }) {
  const { toast } = useToast();
  const [jobs, setJobs] = useState<{ id: number; title: string }[]>([]);
  const [homeJobId, setHomeJobId] = useState<number | ''>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const today = new Date();
    const start = today.toISOString().slice(0, 10);
    const end = new Date(today.getTime() + 7 * 86400000).toISOString().slice(0, 10);
    getEffectiveSchedule(start, end)
      .then(data => {
        setJobs(data.jobs || []);
        const me = (data.employees || []).find(e => e.user_id === userId);
        setHomeJobId(me?.homeJobId ?? '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const change = async (val: string) => {
    const jobId = val ? Number(val) : null;
    setHomeJobId(jobId ?? '');
    setSaving(true);
    try {
      await setHomeJob(userId, jobId);
      toast.success(jobId ? 'Home job set' : 'Home job cleared');
    } catch {
      toast.error('Failed to update home job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-3 mb-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="material-symbols-outlined text-primary text-base">home_work</span>
        <span className="text-sm font-semibold text-gray-800">Home job</span>
        <span className="text-xs text-gray-500">— applies every workday unless a day is overridden</span>
        <Link to="/scheduling" className="ml-auto text-xs text-primary hover:underline">Manage on board →</Link>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <select
          value={homeJobId}
          disabled={loading || saving}
          onChange={e => change(e.target.value)}
          className="h-9 px-2 rounded-lg border border-gray-300 text-sm min-w-[14rem] disabled:opacity-60"
        >
          <option value="">{loading ? 'Loading…' : 'No home job'}</option>
          {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
        </select>
        {saving && <span className="material-symbols-outlined text-base text-gray-400 animate-spin">progress_activity</span>}
      </div>
    </div>
  );
}
